import React, {
  useCallback, useEffect, useMemo, useState,
} from 'react';
import { VStack } from '@gluestack-ui/themed';
import { useTranslation } from 'react-i18next';

import { getGetOralExamCohortStateQueryKey } from '@/common/api/endpoints/oral-exams/oral-exams';
import { OralExamReadiness } from '@/common/api/models/oralExamReadiness';
import type { OralExamReadiness as OralExamReadinessType } from '@/common/api/models/oralExamReadiness';
import {
  OralExamTransitionProgressDialog,
  type OralExamTransitionVariant,
} from '@/common/components/oralExam/OralExamTransitionProgressDialog';
import { useTenantSettingsStore } from '@/common/context/TenantSettingsStore';
import { getOralExamApiErrorMessage } from '@/common/hooks/oralExam/oralExamApiError';
import {
  isHandshakeCanaryChecking,
  isHandshakeCanaryHealthy,
  isHandshakeCanaryUnavailable,
  useHandshakeCanaryStatus,
} from '@/common/hooks/oralExam/useHandshakeCanaryStatus';
import { useMeetingConfirm } from '@/common/components/meeting/MeetingConfirm';
import { useOralExamIdReview } from '@/common/hooks/oralExam/useOralExamIdReview';
import { formatEnglishTime, formatEnglishWaitingSlotStart } from '@/common/utils/formatEnglishDateTime';
import {
  formatOralExamJoinWindowCloseTime,
  isOralExamLecturerJoinWindowClosed,
  ORAL_EXAM_JOIN_LATE_GRACE_MINUTES,
  resolveOralExamWaitingTimePhase,
} from '@/common/utils/oralExam/studentJoinWindow';

import {
  formatStudentNameList,
  isNeverJoinedReadiness,
  resolveStartGateReason,
  type StartGateCopy,
} from './startGateReason';
import {
  StartGateEndedBanner,
  StartGateExaminerAlert,
  StartGateFactLine,
  StartGateHeaderRow,
  type StartGateFact,
  type StartGateResolvedReason,
  StartGateStatusBar,
} from './StartGateStatusBar';

const UNSET_SUPPORT_EMAIL = '(no support email set)';

/** Scheduled start / join-closed fact for the title row or gate area (one source of truth). */
export function useStartGateFact(
  slotStartIso?: string | null,
  slotEndIso?: string | null,
): StartGateFact | null {
  const { t } = useTranslation();
  const [, setJoinWindowTick] = useState(0);
  useEffect(() => {
    if (!slotStartIso || !slotEndIso) return undefined;
    const id = setInterval(() => setJoinWindowTick((n) => n + 1), 30_000);
    return () => clearInterval(id);
  }, [slotEndIso, slotStartIso]);

  const joinWindowClosed = Boolean(
    slotStartIso
    && slotEndIso
    && isOralExamLecturerJoinWindowClosed(slotStartIso, slotEndIso),
  );
  const timePhase = slotStartIso ? resolveOralExamWaitingTimePhase(slotStartIso) : null;

  if (joinWindowClosed) {
    return {
      testID: 'start-gate-join-window',
      text: t('oralExam.lecturer.startGate.joinClosed', { defaultValue: 'Join closed' }),
    };
  }
  if (timePhase === 'before_start' && slotStartIso) {
    return {
      testID: 'start-gate-scheduled-start',
      text: t('oralExam.lecturer.startGate.scheduledStart', {
        defaultValue: 'Exam starts {{time}}',
        time: formatEnglishWaitingSlotStart(slotStartIso) ?? '',
      }),
    };
  }
  if (timePhase === 'during_grace' && slotStartIso) {
    return {
      testID: 'start-gate-scheduled-start-passed',
      text: t('oralExam.lecturer.startGate.scheduledStartWithGrace', {
        defaultValue: 'Exam start {{time}} (+{{minutes}} min late join grace time)',
        time: formatEnglishWaitingSlotStart(slotStartIso) ?? '',
        minutes: ORAL_EXAM_JOIN_LATE_GRACE_MINUTES,
      }),
    };
  }
  return null;
}

export type StartGateReadinessItem = {
  bookingId: string;
  studentName: string;
  isReady: boolean;
  readiness?: OralExamReadinessType;
};

/** The gate's ready count: only `ready_to_start` students count. */
export function isStartGateItemReady(item: StartGateReadinessItem): boolean {
  return item.readiness === OralExamReadiness.ready_to_start;
}

export type StartGateProps = {
  slotId: string;
  readiness: StartGateReadinessItem[];
  /** Slot capacity; capacity 1 renders a single Start button. */
  capacity?: number;
  slotStartIso?: string | null;
  slotEndIso?: string | null;
  supervisorNoShowAt?: string | null;
  technicalCancelledAt?: string | null;
  etag?: string | null;
  onStarted?: () => void;
  /** When false, the time fact is rendered by the parent title row instead. */
  renderFact?: boolean;
  /**
   * Page title block. The gate then renders as the page header row (title left,
   * reason and button right); the parent shows the ready count elsewhere.
   */
  leading?: React.ReactNode;
};

/**
 * Cohort start gate: readiness summary and Continue to briefing (oral-exam-1n-lecturer-ux §3.3).
 */
export function StartGate({
  slotId,
  readiness,
  capacity,
  slotStartIso,
  slotEndIso,
  supervisorNoShowAt,
  technicalCancelledAt,
  etag,
  onStarted,
  renderFact = true,
  leading,
}: StartGateProps) {
  const { t } = useTranslation();
  const { confirm } = useMeetingConfirm();
  const stateQueryKey = useMemo(
    () => getGetOralExamCohortStateQueryKey(slotId),
    [slotId],
  );
  const {
    startCohort,
    openGroupBriefing,
    cancelSessionTechnical,
    isStarting,
    isOpeningBriefing,
    isCancellingTechnical,
  } = useOralExamIdReview({
    etag,
    stateQueryKey,
  });
  const isSingle = (capacity ?? readiness.length) <= 1;
  const useBriefingPath = !isSingle;
  const needsAiExaminer = !isSingle;
  const {
    data: handshakeCanary,
    isFetched: handshakeFetched,
    isError: handshakeError,
    refetchWithRefresh,
  } = useHandshakeCanaryStatus(needsAiExaminer);
  const canaryGatesStart = handshakeCanary?.gatesStart !== false;
  const handshakeHealthy = !needsAiExaminer
    || !canaryGatesStart
    || isHandshakeCanaryHealthy(handshakeCanary);
  const examinerChecking = needsAiExaminer
    && canaryGatesStart
    && isHandshakeCanaryChecking(handshakeCanary, {
      isFetched: handshakeFetched,
      isError: handshakeError,
    });
  const examinerUnavailable = needsAiExaminer
    && canaryGatesStart
    && isHandshakeCanaryUnavailable(handshakeCanary, {
      isFetched: handshakeFetched,
      isError: handshakeError,
    });
  const rawSupportEmail = useTenantSettingsStore((s) => s.settings?.contact?.supportEmail);
  const supportEmail = rawSupportEmail && rawSupportEmail !== UNSET_SUPPORT_EMAIL
    ? rawSupportEmail
    : undefined;

  const fact = useStartGateFact(slotStartIso, slotEndIso);

  const joinWindowClosed = Boolean(
    slotStartIso
    && slotEndIso
    && isOralExamLecturerJoinWindowClosed(slotStartIso, slotEndIso),
  );

  const isEffectivelyReady = isStartGateItemReady;
  const readyItems = useMemo(
    () => readiness.filter(isEffectivelyReady),
    [readiness, isEffectivelyReady],
  );
  const neverJoinedWhileOpen = useMemo(
    () => (joinWindowClosed
      ? []
      : readiness.filter((item) => isNeverJoinedReadiness(item.readiness))),
    [joinWindowClosed, readiness],
  );
  const enteredNotReadyItems = useMemo(
    () => readiness.filter(
      (item) => !isNeverJoinedReadiness(item.readiness) && !isEffectivelyReady(item),
    ),
    [readiness, isEffectivelyReady],
  );
  const enteredBlocksStart = joinWindowClosed
    ? enteredNotReadyItems.some(
      (item) => item.readiness === OralExamReadiness.awaiting_id_review,
    )
    : enteredNotReadyItems.length > 0;

  const allBookedReady = readiness.length > 0 && readyItems.length === readiness.length;
  const partialStart = !isSingle && !allBookedReady;
  const omittedItems = useMemo(
    () => readiness.filter((item) => !isEffectivelyReady(item)),
    [readiness, isEffectivelyReady],
  );
  const timePhase = slotStartIso ? resolveOralExamWaitingTimePhase(slotStartIso) : null;
  const supervisorNoShowCancelled = Boolean(supervisorNoShowAt);
  const technicalCancelled = Boolean(technicalCancelledAt);
  const sessionEnded = supervisorNoShowCancelled || technicalCancelled;
  const showCancelTechnical = useBriefingPath
    && examinerUnavailable
    && !sessionEnded;
  const canStartReadiness = readyItems.length > 0
    && neverJoinedWhileOpen.length === 0
    && !enteredBlocksStart;
  const canStart = !sessionEnded
    && canStartReadiness
    && handshakeHealthy
    && !examinerUnavailable
    && !examinerChecking;

  const handleExaminerRetry = useCallback(async () => {
    try {
      await refetchWithRefresh();
    } catch {
      // GET error surfaces via hook isError + unavailable Alert.
    }
  }, [refetchWithRefresh]);

  const [transitionOpen, setTransitionOpen] = useState(false);
  const [transitionError, setTransitionError] = useState<string | null>(null);
  const [cancelTechnicalError, setCancelTechnicalError] = useState<string | null>(null);
  const [transitionVariant, setTransitionVariant] = useState<OralExamTransitionVariant>('openBriefing');
  const [isAwaitingTransition, setIsAwaitingTransition] = useState(false);
  const isTransitionPending = isAwaitingTransition || isStarting || isOpeningBriefing;

  let submitLabel = t('oralExam.lecturer.startGate.submit', { defaultValue: 'Start the exam' });
  if (isSingle && !canStartReadiness) {
    submitLabel = t('oralExam.lecturer.startGate.reviewFirst', { defaultValue: 'Approve ID first' });
  } else if (isSingle) {
    submitLabel = t('oralExam.lecturer.startGate.submitSingle', { defaultValue: 'Start exam' });
  } else if (useBriefingPath) {
    submitLabel = t('oralExam.lecturer.startGate.continueToBriefing', {
      defaultValue: 'Continue to briefing',
    });
  }

  const handleStart = useCallback(async () => {
    if (!canStart || isTransitionPending) return;

    if (partialStart) {
      const omittedName = formatStudentNameList(omittedItems.map((item) => item.studentName));
      const confirmed = await confirm({
        title: t('oralExam.lecturer.startGate.partialConfirmTitle', {
          defaultValue: 'Start with {{ready}} of {{total}} students?',
          ready: readyItems.length,
          total: readiness.length,
        }),
        message: t('oralExam.lecturer.startGate.partialConfirmMessage', {
          defaultValue: '{{name}} isn\'t ready and can\'t take part. This counts as an attempt for {{name}}.',
          name: omittedName,
        }),
        confirmLabel: t('oralExam.lecturer.startGate.partialConfirmAccept', {
          defaultValue: 'Start anyway',
        }),
        overlayTestID: 'start-gate-partial-confirm',
        messageTestID: 'start-gate-partial-confirm-message',
      });
      if (!confirmed) return;
    }

    const admittedBookingIds = readyItems.map((item) => item.bookingId);
    const variant: OralExamTransitionVariant = useBriefingPath ? 'openBriefing' : 'startExam';

    setTransitionError(null);
    setTransitionVariant(variant);
    setTransitionOpen(true);
    setIsAwaitingTransition(true);

    try {
      if (useBriefingPath) {
        await openGroupBriefing({
          slotId,
          data: {
            admittedBookingIds,
            partialStart,
          },
          etag,
        });
      } else {
        await startCohort({
          slotId,
          data: {
            admittedBookingIds,
            partialStart: false,
          },
          etag,
        });
      }
      setTransitionOpen(false);
      onStarted?.();
    } catch (error) {
      setTransitionError(getOralExamApiErrorMessage(
        error,
        useBriefingPath
          ? t('oralExam.lecturer.startGate.briefingFailed', {
            defaultValue: 'Could not open group briefing. Try again.',
          })
          : t('oralExam.lecturer.startGate.startFailed', {
            defaultValue: 'Could not start the exam. Try again.',
          }),
      ));
    } finally {
      setIsAwaitingTransition(false);
    }
  }, [
    canStart,
    confirm,
    etag,
    isTransitionPending,
    omittedItems,
    onStarted,
    openGroupBriefing,
    partialStart,
    readiness.length,
    readyItems,
    slotId,
    startCohort,
    t,
    useBriefingPath,
  ]);

  const handleCloseTransition = useCallback(() => {
    if (isTransitionPending) return;
    setTransitionOpen(false);
    setTransitionError(null);
  }, [isTransitionPending]);

  const handleCancelTechnical = useCallback(async () => {
    if (!showCancelTechnical || isCancellingTechnical) return;

    const confirmed = await confirm({
      title: t('oralExam.lecturer.startGate.cancelTechnicalConfirmTitle', {
        defaultValue: 'Cancel this session?',
      }),
      message: t('oralExam.lecturer.startGate.cancelTechnicalConfirmMessage', {
        defaultValue:
          'The AI examiner is unavailable. Students will be asked to reschedule. This does not count as an attempt.',
      }),
      confirmLabel: t('oralExam.lecturer.startGate.cancelTechnicalConfirmAccept', {
        defaultValue: 'Cancel session',
      }),
      overlayTestID: 'start-gate-cancel-technical-confirm',
      messageTestID: 'start-gate-cancel-technical-confirm-message',
    });
    if (!confirmed) return;

    setCancelTechnicalError(null);
    try {
      await cancelSessionTechnical({ slotId, etag });
    } catch (error) {
      setCancelTechnicalError(getOralExamApiErrorMessage(
        error,
        t('oralExam.lecturer.startGate.cancelTechnicalFailed', {
          defaultValue: 'Could not cancel the session. Try again.',
        }),
      ));
    }
  }, [
    cancelSessionTechnical,
    confirm,
    etag,
    isCancellingTechnical,
    showCancelTechnical,
    slotId,
    t,
  ]);

  const translate = (copy: StartGateCopy): string => t(copy.key, {
    defaultValue: copy.defaultValue,
    ...copy.values,
  });

  const reason = resolveStartGateReason({
    readiness,
    timePhase,
    joinWindowClosed,
    examinerChecking,
    graceEndTime: slotStartIso ? formatOralExamJoinWindowCloseTime(slotStartIso) : undefined,
  });
  const resolvedReason: StartGateResolvedReason | null = reason
    ? {
      kind: reason.kind,
      testID: reason.testID,
      primary: translate(reason.primary),
    }
    : null;

  let endedBanner: { testID: string; message: string } | null = null;
  if (supervisorNoShowAt) {
    endedBanner = {
      testID: 'start-gate-supervisor-no-show',
      message: t('oralExam.lecturer.startGate.supervisorNoShowCancelled', {
        defaultValue:
          'This session was cancelled at {{cancelTime}} because no supervisor had joined. Students were told it does not count as an attempt.',
        cancelTime: formatEnglishTime(supervisorNoShowAt),
      }),
    };
  } else if (technicalCancelledAt) {
    endedBanner = {
      testID: 'start-gate-technical-cancelled',
      message: t('oralExam.lecturer.startGate.technicalCancelled', {
        defaultValue:
          'This session was cancelled at {{cancelTime}} because the AI examiner was unavailable. Students were told it does not count as an attempt.',
        cancelTime: formatEnglishTime(technicalCancelledAt),
      }),
    };
  }

  const examinerAlert = examinerUnavailable ? (
    <StartGateExaminerAlert
      message={supportEmail
        ? t('oralExam.lecturer.startGate.examinerUnavailableWithEmail', {
          defaultValue:
            'AI Examiner is not available. You cannot proceed with this exam. Please contact support at {{email}}.',
          email: supportEmail,
        })
        : t('oralExam.lecturer.startGate.examinerUnavailable', {
          defaultValue:
            'AI Examiner is not available. You cannot proceed with this exam. Please contact support.',
        })}
      onRetry={() => { handleExaminerRetry(); }}
      showCancelTechnical={showCancelTechnical}
      isCancellingTechnical={isCancellingTechnical}
      onCancelTechnical={() => { handleCancelTechnical(); }}
      cancelTechnicalError={cancelTechnicalError}
    />
  ) : null;
  const statusBar = (
    <StartGateStatusBar
      readyCount={readyItems.length}
      total={readiness.length}
      reason={resolvedReason}
      submitLabel={submitLabel}
      submitDisabled={!canStart || isTransitionPending}
      onSubmit={() => { handleStart(); }}
      leading={leading}
    />
  );
  const endedNode = endedBanner ? (
    <StartGateEndedBanner testID={endedBanner.testID} message={endedBanner.message} />
  ) : null;

  let body: React.ReactNode;
  if (leading) {
    body = endedNode ? (
      <>
        <StartGateHeaderRow leading={leading} />
        {endedNode}
      </>
    ) : (
      <>
        {statusBar}
        {examinerAlert}
      </>
    );
  } else {
    body = endedNode ?? (
      <>
        {examinerAlert}
        {renderFact && fact ? <StartGateFactLine fact={fact} /> : null}
        {statusBar}
      </>
    );
  }

  return (
    <>
      <VStack space={leading ? 'xl' : 'sm'} testID="start-gate">
        {body}
      </VStack>

      <OralExamTransitionProgressDialog
        isOpen={transitionOpen}
        variant={transitionVariant}
        isPending={isTransitionPending && !transitionError}
        error={transitionError}
        onRetry={() => { handleStart(); }}
        onClose={handleCloseTransition}
      />
    </>
  );
}
